import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Download } from "lucide-react";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/28xtkcxe_logo-1.png";

// Editorial Lydia portraits reused from existing brand assets
// HERO = cropped centred portrait
const HERO_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/p1ul12n3_Untitled%20design%20%2829%29.png";
// MIDDLE = full-length London shoot
const EDITORIAL_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/dxodctn9_LydiaLondonShoot2025byOrphee-75%20%281%29.jpg";
// BIO = previously the hero (palm-room seated portrait)
const ABOUT_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/4zkjks13_lydia_portrait.jpg";

// Press logos shared with Faith Over Force
const PRESS = [
  { name: "TEDx", src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/bmhcwz1y_tedx-1.png" },
  { name: "BBC", src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/mi8oya18_bbc-logo-1.png" },
  { name: "Happy Place Festival", src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/d5trnnjs_Happy-Place-Festival-Logo.png", brighten: true },
  { name: "The Times", src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/lsbalefj_The-Times.png" },
  { name: "Hello!", src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/05zcuw3s_Hello.png" },
  { name: "Marie Claire", src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/tc3us6oh_marie-claire-logo.png" },
  { name: "Metro", src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/i80kxbvq_metro-logo.png" },
  { name: "Health & Fitness", src: "https://customer-assets.emergentagent.com/job_faith-over-force/artifacts/0egzj0m1_health-fitness.png" },
];

// GA4 helper
const trackEvent = (name, params = {}) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", name, { programme: "the_peace_project", ...params });
  }
};

const CREDENTIALS = [
  { stat: "16", label: "Years coaching senior women" },
  { stat: "MCC", label: "ICF Master Certified Coach" },
  { stat: "TEDx", label: "Speaker on leadership" },
  { stat: "BBC", label: "Featured contributor" },
  { stat: "50", label: "Senior HR women in current research" },
];

const ORG_GAINS = [
  {
    title: "Clearer thinking at the top",
    body:
      "Decisions that took slightly longer return to the pace and quality your organisation knew her for.",
  },
  {
    title: "A leader who stays",
    body:
      "Retention of a senior woman it took decades to develop, with the institutional knowledge and culture-holding only she can offer.",
  },
  {
    title: "Embodied wellbeing",
    body:
      "She stops performing wellness and starts living it. Her team feels it. The culture quietly reflects it.",
  },
  {
    title: "Reduced organisational risk",
    body:
      "The quiet erosion that nobody is tracking on a dashboard, addressed before it becomes a leave of absence or a resignation.",
  },
];

// ---------- Nav (minimal, brochure CTA only) ----------
const PageNav = ({ scrollToBrochure }) => (
  <header
    data-testid="tpp-nav"
    className="sticky top-0 z-50 bg-ivory/90 backdrop-blur-md border-b border-espresso/10"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
      <a href="#top" data-testid="tpp-nav-logo" className="block leading-none">
        <img
          src={LOGO_URL}
          alt="Lydia Kimmerling"
          className="h-12 md:h-14 w-auto"
          style={{ filter: "invert(1)", mixBlendMode: "multiply" }}
        />
      </a>
      <p className="hidden md:block font-sans text-[10px] uppercase tracking-[0.36em] text-mocha">
        The Peace Project
      </p>
      <button
        type="button"
        onClick={scrollToBrochure}
        data-testid="tpp-nav-brochure-btn"
        className="btn-primary"
      >
        <span className="hidden sm:inline">Download the Brochure</span>
        <span className="sm:hidden">Brochure</span>
        <Download className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  </header>
);

// ---------- Hero ----------
const Hero = ({ scrollToBrochure }) => (
  <section id="top" className="relative bg-ivory overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-16 pb-20 md:pt-24 md:pb-28 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-7 order-2 lg:order-1">
        <Reveal>
          <p className="font-sans text-xs uppercase tracking-[0.36em] text-mocha mb-7">
            A Coaching Programme for Organisations
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <h1
            data-testid="tpp-hero-headline"
            className="font-serif text-espresso leading-[0.95] tracking-tight text-[3rem] sm:text-[4rem] md:text-[5.4rem] lg:text-[6.4rem]"
          >
            The <em className="italic font-medium">Peace</em> Project.
          </h1>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-9 font-sans text-lg md:text-xl text-espresso/85 max-w-2xl leading-relaxed">
            A twelve week coaching programme for senior women in HR and People leadership,
            designed for organisations who want to invest in the people who hold everything
            together.
          </p>
        </Reveal>
        <Reveal delay={0.22}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={scrollToBrochure}
              data-testid="tpp-hero-brochure-btn"
              className="btn-primary"
            >
              Download the Brochure
              <Download className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <a href="#programme" className="btn-outline" data-testid="tpp-hero-learn-more-btn">
              The Case for It
            </a>
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-5 order-1 lg:order-2 relative">
        <Reveal delay={0.12}>
          <div className="relative">
            <img
              src={HERO_PORTRAIT}
              alt="Lydia Kimmerling"
              data-testid="tpp-hero-portrait"
              className="relative w-full aspect-square object-cover"
              loading="eager"
            />
          </div>
        </Reveal>
      </div>
    </div>

    {/* Credentials bar */}
    <div className="bg-espresso">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <p className="font-sans text-[10px] uppercase tracking-[0.36em] text-mocha mb-7 text-center md:text-left">
          Designed by
        </p>
        <div
          data-testid="tpp-credentials"
          className="grid grid-cols-2 md:grid-cols-5 gap-y-8 gap-x-6"
        >
          {CREDENTIALS.map((c) => (
            <div key={c.label} className="flex flex-col items-center md:items-start text-center md:text-left">
              <p className="font-serif text-3xl md:text-4xl text-ivory leading-none">{c.stat}</p>
              <p className="mt-3 font-sans text-[10px] uppercase tracking-[0.28em] text-ivory/60">
                {c.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------- The woman you cannot afford to lose ----------
const TheWoman = () => (
  <section id="programme" className="bg-ivory">
    <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <p className="font-sans text-xs uppercase tracking-[0.36em] text-mocha mb-6">
              The Case for It
            </p>
            <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-[3.6rem] leading-[1.05] text-espresso">
              The woman you cannot afford <em className="italic">to lose.</em>
            </h2>
          </div>
        </Reveal>
        <div className="lg:col-span-7 space-y-7">
          <Reveal delay={0.06}>
            <p className="font-sans text-base md:text-lg text-espresso/85 leading-relaxed">
              She is one of the most capable people in your organisation. She holds the people
              strategy, shapes the culture and carries the emotional weight of your workforce
              with a skill most leaders cannot name, let alone replicate. She has probably been
              doing it for twenty years, and she is extraordinary at it.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-serif italic text-2xl md:text-3xl text-espresso leading-snug border-l-2 border-mocha pl-6">
              She has also not properly switched off in just as long.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="font-sans text-base md:text-lg text-espresso/85 leading-relaxed">
              You will not see it in her output, she is far too good for that, but it is there in
              the decisions that take slightly longer than they should, in the meetings where she
              is present but not quite there, in the slow erosion of the very thing that makes her
              exceptional, her ability to be genuinely, fully human in a role that demands it
              every single day.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Editorial portrait break ----------
const PortraitBreak = () => (
  <section className="bg-ivory">
    <Reveal>
      <img
        src={EDITORIAL_PORTRAIT}
        alt="Lydia Kimmerling, editorial portrait"
        data-testid="tpp-portrait-break"
        className="w-full h-[55vh] md:h-[70vh] object-cover"
        style={{ objectPosition: "center 25%" }}
        loading="lazy"
      />
    </Reveal>
  </section>
);

// ---------- What nobody is talking about ----------
const NobodyTalking = () => (
  <section className="relative bg-espresso text-ivory grain">
    <div className="relative max-w-5xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
      <Reveal>
        <p className="font-sans text-xs uppercase tracking-[0.36em] text-mocha mb-8">
          What nobody is talking about
        </p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-serif text-[2.3rem] md:text-5xl lg:text-[3.6rem] leading-[1.05] max-w-4xl">
          What happens at home does not <em className="italic">stay there.</em>
        </h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="mt-10 font-sans text-base md:text-lg text-ivory/85 max-w-3xl leading-relaxed">
          The tension she carries from a week of holding everyone else together walks straight
          back into your building on Monday morning, and perimenopause, which most organisations
          still do not talk about openly, is quietly affecting the cognitive performance,
          emotional regulation and stress resilience of some of your most senior women right now.
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-12 font-serif italic text-2xl md:text-3xl lg:text-[2.4rem] text-ivory leading-snug max-w-3xl">
          None of this is weakness. All of it is addressable.
        </p>
      </Reveal>
    </div>
  </section>
);

// ---------- What your organisation gains ----------
const OrgGains = () => (
  <section className="bg-cream">
    <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
      <Reveal>
        <p className="font-sans text-xs uppercase tracking-[0.36em] text-mocha mb-8">
          What your organisation gains
        </p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-serif text-[2.4rem] md:text-5xl lg:text-[3.6rem] leading-[1.05] text-espresso max-w-4xl">
          A senior leader who thinks more clearly, leads more presently <em className="italic">and stays.</em>
        </h2>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="mt-10 font-sans text-base md:text-lg text-espresso/80 max-w-3xl leading-relaxed">
          That is the short version. The longer version is that the woman who has done this work
          stops performing wellness and starts embodying it, her team feels it, the culture
          reflects it, and you stop carrying the quiet risk of losing someone who took decades to
          develop.
        </p>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-2 gap-x-12 gap-y-10 border-t border-espresso/15 pt-12" data-testid="tpp-org-gains">
        {ORG_GAINS.map((g, i) => (
          <Reveal key={g.title} delay={i * 0.06}>
            <article className="border-l-2 border-mocha pl-6">
              <h3 className="font-serif text-xl md:text-2xl text-espresso leading-snug">
                {g.title}
              </h3>
              <p className="mt-3 font-sans text-base md:text-lg text-espresso/80 leading-relaxed">
                {g.body}
              </p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Testimonials ----------
// Thin banner testimonial — slim full-width strip
const TestimonialBanner = ({ quote, name, title, testId }) => (
  <section className="bg-mocha text-ivory" data-testid={testId}>
    <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-14">
      <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
        <p className="font-serif text-ivory/80 text-4xl leading-none shrink-0">"</p>
        <blockquote className="font-serif italic text-lg md:text-xl lg:text-2xl leading-snug text-ivory flex-1">
          {quote}
        </blockquote>
        <figcaption className="shrink-0 md:text-right md:border-l md:border-ivory/25 md:pl-8">
          <p className="font-serif text-base md:text-lg text-ivory">{name}</p>
          <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.28em] text-ivory/70">
            {title}
          </p>
        </figcaption>
      </div>
    </div>
  </section>
);

// As Seen On — press logos shared with Faith Over Force
const AsSeenOn = () => (
  <section className="bg-[#3F352F]" data-testid="tpp-press">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-14 md:py-16">
      <p className="font-sans text-[10px] uppercase tracking-[0.36em] text-ivory/55 mb-8 text-center md:text-left">
        As Seen On
      </p>
      <div className="flex flex-wrap items-center justify-center md:justify-between gap-x-10 md:gap-x-8 lg:gap-x-12 gap-y-6">
        {PRESS.map((p) => (
          <img
            key={p.name}
            src={p.src}
            alt={p.name}
            className="h-10 md:h-12 lg:h-14 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
            style={{
              maxWidth: "180px",
              filter: p.brighten ? "brightness(4) contrast(1.1)" : undefined,
            }}
          />
        ))}
      </div>
    </div>
  </section>
);

// ---------- About Lydia ----------
const About = () => (
  <section className="bg-cream">
    <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-40">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <Reveal className="lg:col-span-5">
          <img
            src={ABOUT_PORTRAIT}
            alt="Lydia Kimmerling"
            data-testid="tpp-about-portrait"
            className="w-full aspect-[4/5] object-cover"
            loading="lazy"
          />
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-sans text-xs uppercase tracking-[0.36em] text-mocha mb-6">
              About Lydia Kimmerling
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-[2.2rem] md:text-5xl lg:text-[3.2rem] leading-[1.05] text-espresso">
              Sixteen years inside the lives of <em className="italic">senior women in leadership.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 font-sans text-base md:text-lg text-espresso/85 leading-relaxed">
              Lydia is an MCC ICF Certified Master Life Coach, speaker and coach educator with
              sixteen years inside the lives of senior women in leadership. She has delivered a
              TEDx talk, appeared on the BBC and created an ICF accredited Level 2 certification
              course training the next generation of life coaches in her own methodology. She
              served as resident coach at Rosebar, the longevity clinic at Six Senses Ibiza,
              where she sat with high performing women at some of the most honest moments of
              their lives.
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 font-sans text-base md:text-lg text-espresso/85 leading-relaxed">
              She is currently leading original research with fifty senior women in HR exploring
              what high performance is really costing the people who deliver it. The Peace
              Project is the result. Sixteen years of work and a new body of research, distilled
              into the programme she believes is the most important one she has ever created.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

// ---------- Brochure download (email capture) ----------
const BROCHURE_PDF_URL = "/the-peace-project-brochure.pdf";
const BROCHURE_COVER_URL = "/peace-project-cover.jpg";

const Brochure = ({ innerRef }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = BROCHURE_PDF_URL;
    a.download = "The-Peace-Project-Brochure.pdf";
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: name || null,
        email,
        message: [organisation && `Organisation: ${organisation}`, role && `Role: ${role}`]
          .filter(Boolean)
          .join(" | ") || null,
        source: "peace_project_brochure",
      });
      setSubmitted(true);
      toast.success("Your download has started. The brochure is also on its way to your inbox.");
      trackEvent("brochure_request", { source: "peace_project_brochure" });
      // Auto-trigger PDF download immediately so the user gets it without waiting for email
      setTimeout(triggerDownload, 200);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      ref={innerRef}
      className="relative bg-espresso text-ivory"
      data-testid="tpp-brochure"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Brochure cover preview */}
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div
                className="absolute -inset-4 bg-mocha/25 translate-x-3 translate-y-3"
                aria-hidden
              />
              <img
                src={BROCHURE_COVER_URL}
                alt="The Peace Project brochure cover"
                data-testid="tpp-brochure-cover"
                className="relative w-full h-auto object-cover shadow-2xl"
                loading="lazy"
              />
              <p className="mt-5 font-sans text-[10px] uppercase tracking-[0.32em] text-ivory/60 text-center">
                The Peace Project · Brochure · 2026
              </p>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.08} className="lg:col-span-7">
            <p className="font-sans text-xs uppercase tracking-[0.36em] text-mocha mb-6">
              Find out more
            </p>
            <h2 className="font-serif text-[2.2rem] md:text-5xl lg:text-[3rem] leading-[1.05]">
              Everything you need to know is in <em className="italic">the brochure.</em>
            </h2>
            <p className="mt-6 font-sans text-base md:text-lg text-ivory/85 leading-relaxed">
              The programme, the investment and how to bring it into your organisation. Leave
              your details and we will send it across.
            </p>

            {submitted ? (
              <div
                className="mt-8 bg-ivory/5 border border-ivory/15 p-8 md:p-10"
                data-testid="tpp-brochure-success"
              >
                <p className="font-serif italic text-2xl md:text-3xl text-ivory">
                  Thank you.
                </p>
                <p className="mt-4 font-sans text-base text-ivory/80 leading-relaxed">
                  Your brochure download should be starting now. We have also sent a copy to your
                  inbox. If you would like to start a conversation directly, just reply to the
                  email when it lands.
                </p>
                <button
                  type="button"
                  onClick={triggerDownload}
                  data-testid="tpp-brochure-redownload-btn"
                  className="btn-primary btn-light mt-8"
                >
                  Download Again
                  <Download className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="mt-8 space-y-4"
                data-testid="tpp-brochure-form"
              >
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  data-testid="tpp-brochure-name"
                  className="w-full bg-transparent border border-ivory/25 px-5 py-4 font-sans text-sm text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Work email"
                  data-testid="tpp-brochure-email"
                  className="w-full bg-transparent border border-ivory/25 px-5 py-4 font-sans text-sm text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={organisation}
                    onChange={(e) => setOrganisation(e.target.value)}
                    placeholder="Organisation"
                    data-testid="tpp-brochure-org"
                    className="w-full bg-transparent border border-ivory/25 px-5 py-4 font-sans text-sm text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory"
                  />
                  <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="Your role"
                    data-testid="tpp-brochure-role"
                    className="w-full bg-transparent border border-ivory/25 px-5 py-4 font-sans text-sm text-ivory placeholder:text-ivory/40 focus:outline-none focus:border-ivory"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="tpp-brochure-submit"
                  className="btn-primary btn-light w-full sm:w-auto disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Download the Brochure"}
                  <Download className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <p className="font-sans text-[11px] text-ivory/50 leading-relaxed pt-2">
                  We will only use your details to send the brochure and any follow up directly
                  related to The Peace Project. No marketing list, no noise.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ---------- Footer ----------
const Footer = () => (
  <footer className="bg-espresso text-ivory/65 border-t border-ivory/10" data-testid="tpp-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 flex flex-col md:flex-row gap-6 items-center justify-between">
      <div className="text-center md:text-left">
        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
          <img
            src={LOGO_URL}
            alt="Lydia Kimmerling"
            className="h-10 md:h-12 w-auto mx-auto md:mx-0"
            style={{ mixBlendMode: "screen" }}
          />
          <span className="hidden md:inline text-mocha">·</span>
          <p className="font-sans text-xs uppercase tracking-[0.32em] text-ivory/70">
            The Peace Project
          </p>
          <span className="hidden md:inline text-mocha">·</span>
          <a
            href="mailto:hello@lydiakimmerling.com"
            data-testid="tpp-contact-email"
            onClick={() => trackEvent("email_click")}
            className="font-sans text-xs uppercase tracking-[0.32em] text-ivory/70 hover:text-ivory transition-colors link-underline"
          >
            hello@lydiakimmerling.com
          </a>
        </div>
      </div>
      <p className="font-sans text-xs uppercase tracking-[0.28em]">
        © {new Date().getFullYear()} Lydia Kimmerling
      </p>
    </div>
  </footer>
);

// ---------- Page ----------
const PeaceProject = () => {
  const brochureRef = React.useRef(null);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "The Peace Project | Lydia Kimmerling";
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_title: "The Peace Project",
        page_path: "/the-peace-project",
      });
    }
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const scrollToBrochure = () => {
    brochureRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    trackEvent("brochure_cta_click");
  };

  return (
    <main className="bg-ivory text-espresso overflow-x-hidden">
      <Toaster position="top-center" richColors closeButton />
      <PageNav scrollToBrochure={scrollToBrochure} />
      <Hero scrollToBrochure={scrollToBrochure} />
      <TheWoman />
      <PortraitBreak />
      <NobodyTalking />
      <OrgGains />
      <TestimonialBanner
        quote="You have no idea how much you are helping me. I will be forever grateful."
        name="Carolin"
        title="Former Head of Talent Management, United Nations"
        testId="tpp-testimonial-1"
      />
      <About />
      <TestimonialBanner
        quote="I can't believe that I responded peacefully to a crisis at work today and I haven't taken it home with me."
        name="Rebecca"
        title="HR Director, UK"
        testId="tpp-testimonial-2"
      />
      <Brochure innerRef={brochureRef} />
      <AsSeenOn />
      <Footer />
    </main>
  );
};

export default PeaceProject;
